import { TRPCClientErrorLike } from "@trpc/client";
import { UseTRPCQueryResult } from "@trpc/react-query/shared";
import { useState } from "react";

import type {
  ICardImportCancelInput,
  ICardImportCardStatusesInput,
  ICardImportCardStatusesPage,
  ICardImportCreateUploadInput,
  ICardImportDeleteHistoryResult,
  ICardImportDetail,
  ICardImportDetailInput,
  ICardImportHistoryList,
  IUserImportState,
} from "@monorepo/core";
import type { AppRouter } from "@monorepo/lambda-trpc";

import usePresentToast from "./present-toast.hook";
import { ReactQueryOptions, trpc } from "../utils/trpc";

const uploadFileToPresignedUrl = async ({
  file,
  uploadUrl,
  uploadMethod,
  uploadHeaders,
}: {
  file: File;
  uploadUrl: string;
  uploadMethod: "PUT";
  uploadHeaders: Record<string, string>;
}) => {
  const response = await fetch(uploadUrl, {
    method: uploadMethod,
    headers: uploadHeaders,
    body: file,
  });

  if (response.ok) {
    return;
  }

  const body = await response.text();
  throw new Error(
    `Failed to upload CSV: ${response.status} ${response.statusText}${body ? ` ${body}` : ""}`.trim(),
  );
};

export function useCardImportHistory(
  options?: ReactQueryOptions["cardImports"]["list"],
): UseTRPCQueryResult<ICardImportHistoryList, TRPCClientErrorLike<AppRouter>> {
  return trpc.cardImports.list.useQuery(undefined, {
    refetchOnWindowFocus: false,
    ...options,
  });
}

export function useCardImportDetail(
  importId?: string,
  options?: ReactQueryOptions["cardImports"]["detail"],
): UseTRPCQueryResult<ICardImportDetail, TRPCClientErrorLike<AppRouter>> {
  const input: ICardImportDetailInput = {
    importId: importId ?? "",
  };

  return trpc.cardImports.detail.useQuery(input, {
    enabled: Boolean(importId),
    refetchOnWindowFocus: false,
    ...options,
  });
}

export function useCardImportCardStatuses(
  input?: ICardImportCardStatusesInput,
  options?: ReactQueryOptions["cardImports"]["cardStatuses"],
): UseTRPCQueryResult<
  ICardImportCardStatusesPage,
  TRPCClientErrorLike<AppRouter>
> {
  return trpc.cardImports.cardStatuses.useQuery(
    {
      importId: input?.importId ?? "",
      cursor: input?.cursor,
      pageSize: input?.pageSize,
    },
    {
      enabled: Boolean(input?.importId),
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      ...options,
    },
  );
}

export function useCreateCardImport() {
  const utils = trpc.useUtils();
  const { presentError, presentSuccess } = usePresentToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const createUploadMutation = trpc.cardImports.createUpload.useMutation();
  const completeUploadMutation = trpc.cardImports.completeUpload.useMutation();
  const cancelUploadMutation = trpc.cardImports.cancelUpload.useMutation();

  const invalidateImportQueries = async () => {
    await Promise.all([
      utils.cardImports.list.invalidate(),
      utils.cardImports.detail.invalidate(),
      utils.cardImports.cardStatuses.invalidate(),
    ]);
  };

  const createFileImport = async (file: File): Promise<IUserImportState> => {
    let reservedImportId: string | undefined;
    let uploadStage: "RESERVING" | "UPLOADING" | "QUEUING" = "RESERVING";

    try {
      setIsSubmitting(true);
      const createUploadInput: ICardImportCreateUploadInput = {
        fileName: file.name,
        fileSizeBytes: file.size,
        contentType: file.type || "text/csv",
      };
      const uploadReservation =
        await createUploadMutation.mutateAsync(createUploadInput);
      reservedImportId = uploadReservation.importState.importId;

      uploadStage = "UPLOADING";
      await uploadFileToPresignedUrl({
        file,
        uploadUrl: uploadReservation.uploadUrl,
        uploadMethod: uploadReservation.uploadMethod,
        uploadHeaders: uploadReservation.uploadHeaders,
      });

      uploadStage = "QUEUING";
      const finalState = await completeUploadMutation.mutateAsync({
        importId: uploadReservation.importState.importId ?? "",
      });

      await invalidateImportQueries();
      presentSuccess("Card import queued.");
      return finalState;
    } catch (error) {
      if (reservedImportId && uploadStage === "UPLOADING") {
        await cancelUploadMutation
          .mutateAsync({
            importId: reservedImportId,
            reason: "Upload did not complete.",
          })
          .catch(() => undefined);
      }

      await invalidateImportQueries();
      presentError(
        error instanceof Error ? error.message : "Failed to import cards.",
      );
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    createFileImport,
    isSubmitting,
  };
}

export function useCancelCardImport() {
  const utils = trpc.useUtils();
  const { presentError, presentSuccess } = usePresentToast();
  const [isCanceling, setIsCanceling] = useState(false);
  const cancelUploadMutation = trpc.cardImports.cancelUpload.useMutation();

  const cancelImport = async ({
    importId,
    reason,
  }: ICardImportCancelInput): Promise<IUserImportState> => {
    try {
      setIsCanceling(true);
      const importState = await cancelUploadMutation.mutateAsync({
        importId,
        reason,
      });
      await Promise.all([
        utils.cardImports.list.invalidate(),
        utils.cardImports.detail.invalidate(),
        utils.cardImports.cardStatuses.invalidate(),
      ]);
      presentSuccess("Import canceled.");
      return importState;
    } catch (error) {
      presentError(
        error instanceof Error ? error.message : "Failed to cancel import.",
      );
      throw error;
    } finally {
      setIsCanceling(false);
    }
  };

  return {
    cancelImport,
    isCanceling,
  };
}

export function useDeleteCardImportHistory() {
  const utils = trpc.useUtils();
  const { presentError, presentSuccess } = usePresentToast();
  const [isDeleting, setIsDeleting] = useState(false);
  const deleteHistoryMutation = trpc.cardImports.deleteHistory.useMutation();

  const deleteHistory = async (importId: string) => {
    try {
      setIsDeleting(true);
      const deletedImport: ICardImportDeleteHistoryResult =
        await deleteHistoryMutation.mutateAsync({
          importId,
        });
      await Promise.all([
        utils.cardImports.list.invalidate(),
        utils.cardImports.detail.invalidate(),
        utils.cardImports.cardStatuses.invalidate(),
      ]);
      presentSuccess("Import deleted.");
      return deletedImport;
    } catch (error) {
      presentError(
        error instanceof Error
          ? error.message
          : "Failed to delete import history.",
      );
      throw error;
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    deleteHistory,
    isDeleting,
  };
}
